import { Article, Channel, Ticket, User } from '@wix/answers-api';
import { Box } from '@wix/design-system';
import moment from 'moment';
import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';

import 'moment/min/locales';
import { Error } from '../../components/Error';
import Loader from '../../components/Loader';
import { MyActivity } from '../../components/MyActivity';
import { MyProfile } from '../../components/MyProfile';
import { Context } from '../../context';
import { useHistoryContext } from '../../hooks/useRouteHistory';
import { GetWixServerSidePropsFunction, HttpStatusCode, PageType } from '../../types';
import { loginWithAnswersSSO } from '../../utils/login';
import { getWixCustomerId } from '../../utils/session-management';
import { withServerSideProps } from '../../utils/with-server-side-props';

const ProfilePage: NextPage = () => {
  const {
    locale,
    query: { id },
  } = useRouter();
  const { answersApi } = useContext(Context);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errorStatusCode, setErrorStatusCode] = useState(0);
  const [user, setUser] = useState<User>();
  const [customerId, setCustomerId] = useState<string>('');
  const [followedContent, setFollowedContent] = useState<Article[]>();
  const [tickets, setTickets] = useState<Ticket[]>();
  const [followedContentCount, setFollowedContentCount] = useState(0);
  const [ticketsCount, setTicketsCount] = useState(0);
  const [ticketsPage, setTicketsPage] = useState(1);
  const [followedContentPage, setFollowedContentPage] = useState(1);
  const { prePageUrl } = useHistoryContext();

  const formatDate = (data?: number | Date) =>
    moment(data)
      .locale(locale as string)
      .format('LL');

  const errorHandle = (code?: HttpStatusCode) => {
    setIsLoading(false);
    setIsError(true);
    if (code) {
      setErrorStatusCode(code);
    }
  };

  useEffect(() => {
    void (async () => {
      if (user?.id === id) {
        try {
          const newTickets = await answersApi.tickets.getTicketsOfUser({
            channelFilters: [
              { ids: [], channel: Channel.API },
              { ids: [], channel: Channel.EMAIL },
              { ids: [], channel: Channel.OUTBOUND },
              { ids: [], channel: Channel.PHONE_CALLBACK },
              { ids: [], channel: Channel.PHONE_INBOUND },
              { ids: [], channel: Channel.PHONE_OUTBOUND },
              { ids: [], channel: Channel.WEB },
              { ids: [], channel: Channel.WIDGET },
            ],
            locale: locale as string,
            page: ticketsPage,
            pageSize: 10,
          });
          setTickets([...(tickets ? tickets : []), ...newTickets.items]);
          if (newTickets.itemsCount !== 0) {
            setTicketsCount(newTickets.itemsCount);
          }
        } catch (e: any) {
          const code = e?.response?.status || e?.code;
          if (code === HttpStatusCode.UNAUTHORIZED) {
            loginWithAnswersSSO(location.href, locale as string);
          }
          errorHandle(code);
        }
      }
    })();
  }, [ticketsPage, answersApi.tickets, user, id, locale]);

  useEffect(() => {
    void (async () => {
      if (user?.id === id) {
        try {
          const newFollowedContent =
            await answersApi.articles.getFollowedArticles({
              userId: id as string,
              locale: locale as string,
              page: followedContentPage,
              pageSize: 10,
            });

          setFollowedContent([
            ...(followedContent ? followedContent : []),
            ...newFollowedContent.items,
          ]);
          if (newFollowedContent.itemsCount !== 0) {
            setFollowedContentCount(newFollowedContent.itemsCount);
          }
        } catch (e: any) {
          const code = e?.response?.status || e?.code;
          if (code === HttpStatusCode.UNAUTHORIZED) {
            loginWithAnswersSSO(location.href, locale as string);
          }
          errorHandle(code);
        }
      }
    })();
  }, [answersApi.articles, followedContentPage, user, id, locale]);

  useEffect(() => {
    void (async () => {
      try {
        const currentUser = await answersApi.users.me();
        if (id !== currentUser.id) {
          errorHandle(HttpStatusCode.FORBIDDEN);
        } else {
          setUser(currentUser);
          setCustomerId(getWixCustomerId());
          setIsLoading(false);
        }
      } catch (e: any) {
        const code = e?.response?.status || e?.code;
        if (code === HttpStatusCode.UNAUTHORIZED) {
          loginWithAnswersSSO(location.href, locale as string);
        }
        errorHandle(code);
      }
    })();
  }, [id, locale, answersApi.users, prePageUrl]);

  const unfollowArticle = async (articleId: string) => {
    await answersApi.articles.unfollowArticle({
      id: articleId,
      locale: locale as string,
    });
    setFollowedContent(
      followedContent?.filter((article) => article.id !== articleId)
    );

    setFollowedContentCount(followedContentCount - 1);
  };

  const showMoreFollowedContentClick = () =>
    setFollowedContentPage(followedContentPage + 1);
  const showMoreTicketsClick = () => setTicketsPage(ticketsPage + 1);
  return !isError ? (
    !tickets || !followedContent || !user ? null : (
      <Box direction="vertical">
        {isLoading && <Loader />}
        <Box direction="horizontal" flexWrap="wrap" width="100%">
          <MyProfile
            profileImage={user?.profileImage}
            name={user?.firstName || ''}
            joined={formatDate(user?.creationDate)}
            customerId={customerId}
          />
          <MyActivity
            unfollowContnet={unfollowArticle}
            followedContent={followedContent}
            tickets={tickets}
            onShowMoreFollowedContentClick={showMoreFollowedContentClick}
            onShowMoreTicketsClick={showMoreTicketsClick}
            followedContentCount={followedContentCount}
            ticketsCount={ticketsCount}
          />
        </Box>
      </Box>
    )
  ) : (
    <Error statusCode={errorStatusCode} />
  );
};
export const getServerSideProps: GetWixServerSidePropsFunction =
  withServerSideProps(async ({ query }) => {
    const { id } = query;

    return {
      props: {
        pageType: PageType.Profile,
        itemId: id,
      },
    };
  });

export default ProfilePage;
